import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from './utils/apiClient';
import { API_BASE_URL } from './constants/api';
import { enqueueOfflineRequest } from './utils/offlineQueue';
import './styles/DailyReports.css';
import { EQUIPMENT_CATALOG, MATERIAL_CATALOG } from './constants/resourceCatalog';

const API_BASE = API_BASE_URL;

const INITIAL_FORM = (projectId) => ({
  project_id: projectId || '',
  report_date: new Date().toISOString().split('T')[0],
  weather: 'sunny',
  work_summary: '',
  personnel_count: '',
  personnel_details: '',
  equipment_used: '',
  materials_used: '',
  issues_faced: '',
});

const DailyReports = ({ projectId }) => {
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamPickerId, setTeamPickerId] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [teamEmployeesMap, setTeamEmployeesMap] = useState({});
  const [selectedEmployeesByTeam, setSelectedEmployeesByTeam] = useState({});
  const [resources, setResources] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [editingReportId, setEditingReportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncNotice, setSyncNotice] = useState('');
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [aiDraftMeta, setAiDraftMeta] = useState({
    riskTags: [],
    riskSummary: '',
  });
  const [formData, setFormData] = useState(INITIAL_FORM(projectId));
  const [equipmentChoice, setEquipmentChoice] = useState('');
  const [customEquipment, setCustomEquipment] = useState('');
  const [materialChoice, setMaterialChoice] = useState('');
  const [customMaterial, setCustomMaterial] = useState('');

  const resetPersonnelSelections = useCallback(() => {
    setTeamPickerId('');
    setSelectedTeamIds([]);
    setTeamEmployeesMap({});
    setSelectedEmployeesByTeam({});
  }, []);

  const resetFormState = useCallback(() => {
    setFormData(INITIAL_FORM(projectId));
    setAiDraftMeta({ riskTags: [], riskSummary: '' });
    setEquipmentChoice('');
    setMaterialChoice('');
    setCustomEquipment('');
    setCustomMaterial('');
    setEditingReportId(null);
    resetPersonnelSelections();
  }, [projectId, resetPersonnelSelections]);

  const fetchProjects = useCallback(async () => {
    const response = await apiClient.get(`${API_BASE}/projects`);
    setProjects(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchTeams = useCallback(async () => {
    const response = await apiClient.get(`${API_BASE}/teams`);
    setTeams(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchResources = useCallback(async () => {
    const response = await apiClient.get(`${API_BASE}/resources`);
    setResources(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchTeamEmployees = useCallback(
    async (teamId) => {
      if (!teamId) return [];
      const response = await apiClient.get(`${API_BASE}/teams/${teamId}/employees`);
      return Array.isArray(response.data) ? response.data : [];
    },
    []
  );

  const fetchReports = useCallback(async () => {
    const url = projectId ? `${API_BASE}/dailyReports/project/${projectId}` : `${API_BASE}/dailyReports`;
    const response = await apiClient.get(url);
    setReports(Array.isArray(response.data) ? response.data : []);
  }, [projectId]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchProjects(), fetchTeams(), fetchResources(), fetchReports()]);
        if (projectId) {
          setFormData((prev) => ({ ...prev, project_id: projectId }));
        }
      } catch (error) {
        // keep page usable
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, fetchProjects, fetchReports, fetchResources, fetchTeams]);

  const equipmentOptions = useMemo(() => {
    const fromResource = resources.filter((r) => r.type === 'equipment').map((r) => r.name);
    return Array.from(new Set([...EQUIPMENT_CATALOG, ...fromResource]));
  }, [resources]);

  const materialOptions = useMemo(() => {
    const fromResource = resources.filter((r) => r.type === 'material').map((r) => r.name);
    return Array.from(new Set([...MATERIAL_CATALOG, ...fromResource]));
  }, [resources]);

  const selectedEmployeeCount = useMemo(
    () =>
      Object.values(selectedEmployeesByTeam).reduce(
        (total, ids) => total + (Array.isArray(ids) ? ids.length : 0),
        0
      ),
    [selectedEmployeesByTeam]
  );

  const personnelDetailText = useMemo(() => {
    return selectedTeamIds
      .map((teamId) => {
        const selectedIds = selectedEmployeesByTeam[teamId] || [];
        if (selectedIds.length === 0) return null;
        const teamName = teams.find((team) => Number(team.id) === Number(teamId))?.name || `Ekip ${teamId}`;
        const employees = (teamEmployeesMap[teamId] || [])
          .filter((emp) => selectedIds.includes(emp.id))
          .map((emp) => `${emp.name}${emp.position ? ` (${emp.position})` : ''}`);
        if (employees.length === 0) return null;
        return `${teamName}: ${employees.join(', ')}`;
      })
      .filter(Boolean)
      .join(' | ');
  }, [selectedEmployeesByTeam, selectedTeamIds, teamEmployeesMap, teams]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      personnel_count: selectedEmployeeCount || '',
      personnel_details: personnelDetailText,
    }));
  }, [personnelDetailText, selectedEmployeeCount]);

  const appendFieldValue = (field, value) => {
    if (!value) return;
    setFormData((prev) => {
      const current = String(prev[field] || '').trim();
      if (!current) return { ...prev, [field]: value };
      if (current.toLowerCase().includes(String(value).toLowerCase())) return prev;
      return { ...prev, [field]: `${current}, ${value}` };
    });
  };

  const handleCopyFromYesterday = () => {
    if (reports.length === 0) {
      window.alert('Kopyalanacak rapor bulunamadı.');
      return;
    }
    const latestReport = reports[0];
    setFormData((prev) => ({
      ...prev,
      work_summary: latestReport.work_summary || '',
      personnel_count: latestReport.personnel_count || '',
      personnel_details: latestReport.personnel_details || '',
      equipment_used: latestReport.equipment_used || '',
      materials_used: latestReport.materials_used || '',
      issues_faced: latestReport.issues_faced || '',
    }));
    window.alert('Son rapordaki alanlar kopyalandı.');
  };

  const handleGenerateAiDraft = async () => {
    const targetProjectId = formData.project_id || projectId;
    if (!targetProjectId) {
      window.alert('AI taslak için önce proje seçin.');
      return;
    }

    try {
      setCopilotLoading(true);
      const response = await apiClient.post(
        `${API_BASE}/ai/site-copilot/daily-report-draft`,
        {
          project_id: Number(targetProjectId),
          note: formData.work_summary || '',
        }
      );

      const draft = response.data?.draft || {};
      setFormData((prev) => ({
        ...prev,
        project_id: String(draft.project_id || prev.project_id || ''),
        report_date: draft.report_date || prev.report_date,
        weather: draft.weather || prev.weather,
        work_summary: draft.work_summary || prev.work_summary,
        personnel_count: draft.personnel_count || prev.personnel_count,
        personnel_details: draft.personnel_details || prev.personnel_details,
        equipment_used: draft.equipment_used || prev.equipment_used,
        materials_used: draft.materials_used || prev.materials_used,
        issues_faced: draft.issues_faced || prev.issues_faced,
      }));
      setAiDraftMeta({
        riskTags: Array.isArray(response.data?.risk_tags) ? response.data.risk_tags : [],
        riskSummary: response.data?.risk_summary || '',
      });
      setSyncNotice(`AI saha taslağı oluşturuldu. Kaynak foto sayısı: ${Number(response.data?.source_photo_count || 0)}.`);
    } catch (error) {
      window.alert(error.response?.data?.message || 'AI saha taslağı oluşturulamadı.');
    } finally {
      setCopilotLoading(false);
    }
  };

  const addTeamSelection = async () => {
    if (!teamPickerId) return;
    const exists = selectedTeamIds.includes(teamPickerId);
    if (exists) return;

    try {
      const employees = await fetchTeamEmployees(teamPickerId);
      setSelectedTeamIds((prev) => [...prev, teamPickerId]);
      setTeamEmployeesMap((prev) => ({ ...prev, [teamPickerId]: employees }));
      setSelectedEmployeesByTeam((prev) => ({ ...prev, [teamPickerId]: [] }));
      setTeamPickerId('');
    } catch (error) {
      window.alert('Ekip personelleri yüklenemedi.');
    }
  };

  const removeTeamSelection = (teamId) => {
    setSelectedTeamIds((prev) => prev.filter((id) => id !== teamId));
    setTeamEmployeesMap((prev) => {
      const next = { ...prev };
      delete next[teamId];
      return next;
    });
    setSelectedEmployeesByTeam((prev) => {
      const next = { ...prev };
      delete next[teamId];
      return next;
    });
  };

  const toggleEmployeeSelection = (teamId, employeeId) => {
    setSelectedEmployeesByTeam((prev) => {
      const current = prev[teamId] || [];
      const exists = current.includes(employeeId);
      const nextTeamIds = exists ? current.filter((id) => id !== employeeId) : [...current, employeeId];
      return { ...prev, [teamId]: nextTeamIds };
    });
  };

  const openCreateForm = () => {
    resetFormState();
    setShowForm(true);
  };

  const openEditForm = (report) => {
    setEditingReportId(report.id);
    setFormData({
      project_id: report.project_id || projectId || '',
      report_date: report.report_date ? String(report.report_date).split('T')[0] : new Date().toISOString().split('T')[0],
      weather: report.weather || 'sunny',
      work_summary: report.work_summary || '',
      personnel_count: report.personnel_count || '',
      personnel_details: report.personnel_details || '',
      equipment_used: report.equipment_used || '',
      materials_used: report.materials_used || '',
      issues_faced: report.issues_faced || '',
    });
    setAiDraftMeta({ riskTags: [], riskSummary: '' });
    resetPersonnelSelections();
    setEquipmentChoice('');
    setMaterialChoice('');
    setCustomEquipment('');
    setCustomMaterial('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetFormState();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      project_id: formData.project_id || projectId,
      personnel_count: formData.personnel_count || null,
      personnel_details: formData.personnel_details || null,
    };

    try {
      if (editingReportId) {
        await apiClient.put(`${API_BASE}/dailyReports/${editingReportId}`, payload);
      } else {
        await apiClient.post(`${API_BASE}/dailyReports`, payload);
      }
      await fetchReports();
      setSyncNotice('');
      closeForm();
    } catch (error) {
      if (!error.response) {
        enqueueOfflineRequest({
          method: editingReportId ? 'put' : 'post',
          url: editingReportId ? `${API_BASE}/dailyReports/${editingReportId}` : `${API_BASE}/dailyReports`,
          data: payload,
          label: editingReportId ? 'Günlük rapor güncelleme' : 'Günlük rapor kaydı',
        });
        setSyncNotice('Bağlantı olmadığı için rapor saha kuyruğuna alındı. Ağ gelince otomatik gönderilecek.');
        closeForm();
        return;
      }
      window.alert(error.response?.data?.message || 'Rapor kaydedilemedi.');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Bu günlük raporu silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/dailyReports/${reportId}`);
      await fetchReports();
      if (Number(selectedReport?.id) === Number(reportId)) {
        setSelectedReport(null);
      }
    } catch (error) {
      window.alert(error.response?.data?.message || 'Rapor silinemedi.');
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const response = await apiClient.get(`${API_BASE}/dailyReports/export/pdf/${reportId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const disposition = String(response.headers?.['content-disposition'] || '');
      const fromHeader = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      link.href = url;
      link.setAttribute('download', fromHeader || `günlük-rapor-${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      window.alert('PDF indirilemedi.');
    }
  };

  const weatherLabel = (weather) => {
    switch (weather) {
      case 'sunny':
        return 'Güneşli';
      case 'cloudy':
        return 'Bulutlu';
      case 'rainy':
        return 'Yağmurlu';
      case 'snowy':
        return 'Karlı';
      default:
        return '-';
    }
  };

  return (
    <div className="daily-reports-container">
      <div className="reports-header">
        <h3>Şantiye Günlüğü</h3>
        <button onClick={showForm ? closeForm : openCreateForm} className="btn-primary" type="button">
          {showForm ? 'İptal' : 'Yeni Rapor Yaz'}
        </button>
      </div>

      {syncNotice && <div className="success-alert">{syncNotice}</div>}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingReportId ? 'Günlük Raporu Düzenle' : 'Yeni Günlük Rapor'}</h2>
              <button className="btn-close" onClick={closeForm} type="button">X</button>
            </div>

            <div className="form-inline-actions">
              <button type="button" onClick={handleCopyFromYesterday} className="btn-link">
                Dünden Kopyala
              </button>
              <button type="button" onClick={handleGenerateAiDraft} className="btn-link" disabled={copilotLoading}>
                {copilotLoading ? 'AI Taslak Hazırlanıyor...' : 'AI Saha Taslağı'}
              </button>
            </div>

            {(aiDraftMeta.riskTags.length > 0 || aiDraftMeta.riskSummary) && (
              <div className="ai-draft-meta">
                <div className="ai-draft-tags">
                  {aiDraftMeta.riskTags.map((tag) => (
                    <span key={`risk-tag-${tag}`} className="ai-risk-tag">{tag}</span>
                  ))}
                </div>
                {aiDraftMeta.riskSummary && (
                  <p className="ai-draft-summary">{aiDraftMeta.riskSummary}</p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                {!projectId && (
                  <div className="form-group">
                    <label>Proje</label>
                    <select
                      className="form-control"
                      value={formData.project_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, project_id: e.target.value }))}
                      required
                    >
                      <option value="">Proje seçiniz</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Tarih</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.report_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, report_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Hava Durumu</label>
                  <select
                    className="form-control"
                    value={formData.weather}
                    onChange={(e) => setFormData((prev) => ({ ...prev, weather: e.target.value }))}
                  >
                    <option value="sunny">Güneşli</option>
                    <option value="cloudy">Bulutlu</option>
                    <option value="rainy">Yağmurlu</option>
                    <option value="snowy">Karlı</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Yapılan İş Özeti</label>
                <textarea
                  className="form-control"
                  style={{ minHeight: 86 }}
                  placeholder="Sahada tamamlanan işler"
                  value={formData.work_summary}
                  onChange={(e) => setFormData((prev) => ({ ...prev, work_summary: e.target.value }))}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Personel / Ekip</label>
                  <div className="team-picker-row">
                    <select
                      className="form-control"
                      value={teamPickerId}
                      onChange={(e) => setTeamPickerId(e.target.value)}
                    >
                      <option value="">Ekip seçiniz</option>
                      {teams
                        .filter((team) => !selectedTeamIds.includes(String(team.id)))
                        .map((team) => (
                          <option key={team.id} value={String(team.id)}>{team.name}</option>
                        ))}
                    </select>
                    <button type="button" className="btn-link" onClick={addTeamSelection}>
                      Ekip Ekle
                    </button>
                  </div>

                  {selectedTeamIds.length === 0 ? (
                    <div className="personnel-empty-note">Bir veya daha fazla ekip ekleyin.</div>
                  ) : (
                    <div className="team-personnel-groups">
                      {selectedTeamIds.map((teamId) => {
                        const teamName = teams.find((team) => Number(team.id) === Number(teamId))?.name || `Ekip ${teamId}`;
                        const employees = teamEmployeesMap[teamId] || [];
                        const selectedIds = selectedEmployeesByTeam[teamId] || [];
                        return (
                          <div key={teamId} className="team-personnel-card">
                            <div className="team-personnel-header">
                              <strong>{teamName}</strong>
                              <button
                                type="button"
                                className="danger-link"
                                onClick={() => removeTeamSelection(teamId)}
                              >
                                Ekip Çıkar
                              </button>
                            </div>
                            {employees.length === 0 ? (
                              <small className="muted">Bu ekipte personel yok.</small>
                            ) : (
                              <div className="team-personnel-list">
                                {employees.map((employee) => (
                                  <label key={employee.id} className="team-personnel-item">
                                    <input
                                      type="checkbox"
                                      checked={selectedIds.includes(employee.id)}
                                      onChange={() => toggleEmployeeSelection(teamId, employee.id)}
                                    />
                                    <span>{employee.name} {employee.position ? `(${employee.position})` : ''}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="personnel-stats">
                    <input
                      type="number"
                      className="form-control"
                      value={formData.personnel_count}
                      readOnly
                      placeholder="Personel sayısı"
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={formData.personnel_details || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, personnel_details: e.target.value }))}
                      placeholder="Personel detayı (otomatik dolar, isterseniz düzenleyin)"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Kullanılan Ekipman</label>
                  <select
                    className="form-control"
                    value={equipmentChoice}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEquipmentChoice(value);
                        if (value && value !== 'Diğer') appendFieldValue('equipment_used', value);
                    }}
                  >
                    <option value="">Listeden seçin</option>
                    {equipmentOptions.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                  {equipmentChoice === 'Diğer' && (
                    <div className="manual-entry-row">
                      <input
                        type="text"
                        className="form-control"
                        value={customEquipment}
                        onChange={(e) => setCustomEquipment(e.target.value)}
                        placeholder="Ekipman girin"
                      />
                      <button type="button" className="btn-link" onClick={() => appendFieldValue('equipment_used', customEquipment)}>
                        Ekle
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    className="form-control"
                    value={formData.equipment_used}
                    onChange={(e) => setFormData((prev) => ({ ...prev, equipment_used: e.target.value }))}
                    placeholder="Virgülle birden fazla giriş"
                    style={{ marginTop: '0.45rem' }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kullanılan Malzeme</label>
                  <select
                    className="form-control"
                    value={materialChoice}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMaterialChoice(value);
                        if (value && value !== 'Diğer') appendFieldValue('materials_used', value);
                    }}
                  >
                    <option value="">Listeden seçin</option>
                    {materialOptions.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                  {materialChoice === 'Diğer' && (
                    <div className="manual-entry-row">
                      <input
                        type="text"
                        className="form-control"
                        value={customMaterial}
                        onChange={(e) => setCustomMaterial(e.target.value)}
                        placeholder="Malzeme girin"
                      />
                      <button type="button" className="btn-link" onClick={() => appendFieldValue('materials_used', customMaterial)}>
                        Ekle
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    className="form-control"
                    value={formData.materials_used}
                    onChange={(e) => setFormData((prev) => ({ ...prev, materials_used: e.target.value }))}
                    placeholder="Virgülle birden fazla giriş"
                    style={{ marginTop: '0.45rem' }}
                  />
                </div>

                <div className="form-group">
                  <label>Yaşanan Sorunlar</label>
                  <textarea
                    className="form-control"
                    style={{ minHeight: 116 }}
                    value={formData.issues_faced}
                    onChange={(e) => setFormData((prev) => ({ ...prev, issues_faced: e.target.value }))}
                    placeholder="Saha sorunları, gecikmeler, riskler"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingReportId ? 'Raporu Güncelle' : 'Raporu Kaydet'}
                </button>
                <button type="button" className="btn-cancel" onClick={closeForm}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Raporlar yükleniyor...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-gray-500">
            Henüz günlük rapor girilmemiş.
          </div>
        ) : (
          reports.map((report) => (
            <div key={report.id} className="report-card">
              <div className="report-header">
                <div>
                  <h4 className="report-date">
                    {new Date(report.report_date).toLocaleDateString('tr-TR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </h4>
                  <div className="report-meta">
                    Proje: {report.project_name || '-'} - Raporlayan: {report.reported_by_username || '-'}
                  </div>
                </div>
                <div className="weather-badge">{weatherLabel(report.weather)}</div>
              </div>

              <div className="report-body">
                <div className="report-summary">{report.work_summary || '-'}</div>
                <div className="report-stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Personel</span>
                    <span className="stat-value">{report.personnel_count || '-'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Personel Detay</span>
                    <span className="stat-value stat-text">{report.personnel_details || '-'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Ekipman</span>
                    <span className="stat-value stat-text">{report.equipment_used || '-'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Malzeme</span>
                    <span className="stat-value stat-text">{report.materials_used || '-'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Sorun</span>
                    <span className="stat-value stat-text danger">{report.issues_faced || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="report-footer">
                <button onClick={() => setSelectedReport(report)} className="btn-icon" type="button">Detay</button>
                <button onClick={() => openEditForm(report)} className="btn-icon" type="button">Düzenle</button>
                <button onClick={() => handleDownloadPdf(report.id)} className="btn-icon" type="button">PDF</button>
                <button onClick={() => handleDeleteReport(report.id)} className="btn-icon danger" type="button">Sil</button>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Günlük Rapor Detayı</h2>
              <button className="btn-close" type="button" onClick={() => setSelectedReport(null)}>X</button>
            </div>
            <div className="detail-grid">
              <DetailRow label="Proje" value={selectedReport.project_name} />
              <DetailRow label="Raporlayan" value={selectedReport.reported_by_username} />
              <DetailRow label="Tarih" value={new Date(selectedReport.report_date).toLocaleDateString('tr-TR')} />
              <DetailRow label="Hava Durumu" value={weatherLabel(selectedReport.weather)} />
              <DetailRow label="Personel Sayısı" value={selectedReport.personnel_count || '-'} />
              <DetailRow label="Personel Detay" value={selectedReport.personnel_details || '-'} />
              <DetailRow label="Ekipman" value={selectedReport.equipment_used || '-'} />
              <DetailRow label="Malzeme" value={selectedReport.materials_used || '-'} />
              <DetailRow label="Yapılan İş Özeti" value={selectedReport.work_summary || '-'} />
              <DetailRow label="Yaşanan Sorunlar" value={selectedReport.issues_faced || '-'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="detail-row">
    <div className="detail-label">{label}</div>
    <div className="detail-value">{value}</div>
  </div>
);

export default DailyReports;

